/**
 * Search query parser for TCG PWA smart search.
 *
 * Parses natural language queries like:
 * - "pikachu" -> name="pikachu"
 * - "cleffa 150" -> name="cleffa", number="150"
 * - "pikachu OBF" -> name="pikachu", setCode="OBF", setId="sv3"
 * - "dark gyarados 8 TR" -> name="dark gyarados", number="8", setCode="TR", setId="base5"
 */

import type { SetCodeEntry } from './setCodeData';

export interface ParsedQuery {
  /** Card name to search */
  name: string;
  /** Card number (1-4 digits, e.g., "025" or "150") */
  number?: string;
  /** Set code as entered by user (e.g., "OBF") */
  setCode?: string;
  /** Scrydex expansion ID (e.g., "sv3") */
  setId?: string;
  /** Original raw query string */
  raw: string;
  /** Human-readable interpretation for UI display */
  interpretation: string;
}

/**
 * Check if a token is a valid card number (1-4 digits).
 * Card numbers can have leading zeros (e.g., "025").
 */
function isCardNumber(token: string): boolean {
  return /^\d{1,4}$/.test(token);
}

/**
 * Parse a search query into structured components.
 *
 * Algorithm:
 * 1. Tokenize query by whitespace
 * 2. Scan from right-to-left:
 *    - If token matches a set code -> extract as setCode/setId
 *    - If token is 1-4 digits -> extract as number
 *    - Otherwise -> prepend to name
 * 3. Build human-readable interpretation string
 *
 * @param query - Raw search query
 * @param setCodes - Map of uppercase set codes to SetCodeEntry
 * @returns ParsedQuery with structured fields
 */
export function parseSearchQuery(
  query: string,
  setCodes: Map<string, SetCodeEntry>
): ParsedQuery {
  const raw = query;
  const trimmed = query.trim();

  if (!trimmed) {
    return {
      name: '',
      raw,
      interpretation: '',
    };
  }

  const tokens = trimmed.split(/\s+/);

  let name: string[] = [];
  let number: string | undefined;
  let setCode: string | undefined;
  let setId: string | undefined;

  // Scan from right to left
  for (let i = tokens.length - 1; i >= 0; i--) {
    const token = tokens[i];
    const tokenUpper = token.toUpperCase();

    // Check if it's a set code (only if we haven't found one yet)
    if (!setCode && setCodes.has(tokenUpper)) {
      const entry = setCodes.get(tokenUpper)!;
      setCode = token; // Preserve original case for display
      setId = entry.scrydexId;
      continue;
    }

    // Check if it's a card number (only if we haven't found one yet)
    // Also, only treat as number if it's not at the start of the query
    // (e.g., "151 mew" should be name="151 mew", not number="151")
    if (!number && isCardNumber(token) && i > 0) {
      number = token;
      continue;
    }

    // Otherwise, it's part of the name (prepend to maintain order)
    name.unshift(token);
  }

  // If all tokens were consumed as number/set and nothing left for name,
  // treat everything as the name
  if (name.length === 0) {
    return {
      name: trimmed,
      raw,
      interpretation: buildInterpretation(trimmed, undefined, undefined, setCodes),
    };
  }

  const nameStr = name.join(' ');

  return {
    name: nameStr,
    number,
    setCode,
    setId,
    raw,
    interpretation: buildInterpretation(nameStr, number, setCode, setCodes),
  };
}

/**
 * Build a human-readable interpretation string for display.
 */
function buildInterpretation(
  name: string,
  number: string | undefined,
  setCode: string | undefined,
  setCodes: Map<string, SetCodeEntry>
): string {
  const parts: string[] = [];

  // Name part
  parts.push(`'${name}'`);

  // Number part
  if (number) {
    parts.push(`#${number}`);
  }

  // Set part
  if (setCode) {
    const entry = setCodes.get(setCode.toUpperCase());
    const setName = entry?.displayName || setCode;
    return `Searching for ${parts.join(' ')} in ${setName}`;
  }

  return `Searching for ${parts.join(' ')} in all sets`;
}
